<template>
  <Teleport to="body">
    <div
      v-if="visible"
      class="fixed inset-0 z-[11000] flex items-center justify-center bg-black/50"
    >
      <div class="bg-white rounded-lg shadow-lg w-full max-w-2xl p-5">

        <!-- ===================== LOADING (NEW) ===================== -->
        <div
          v-if="mode === 'loading'"
          class="flex flex-col items-center justify-center py-12"
        >
          <dots-loader />
          <p class="text-sm text-gray-600 mt-4 text-center">
            {{ loadingMessage }}
          </p>
        </div>

        <!-- ===================== SUCCESS (NEW) ===================== -->
        <div v-else-if="mode === 'success'" class="text-center">
           <div
              class="h-1 -mt-5 -mx-5 mb-5 rounded-t-lg"
              style="background-color: #1976d2;"
            ></div>
          <div class="flex justify-center mb-4">
            <div
              class="h-14 w-14 rounded-full border-2 flex items-center justify-center" style="border-color: #1976d2;"
            >
              <i class="pi pi-check font-bold" style="color: #1976d2;font-size: 30px;"></i>
            </div>
          </div>

          <h3 class="text-xl font-semibold text-darkblue">
            System Activated Successfully
          </h3>
          <p class="text-sm text-gray-600 mt-2">
            The system has been activated and is now running as expected.
          </p>

          <div class="mt-5 pt-4 text-left">
            <div
              class="flex items-center gap-3 border border-lightgrey rounded-md px-3 py-2.5"
            >
              <div
                class="h-9 w-9 rounded border border-lightgrey flex items-center justify-center shrink-0 bg-white overflow-hidden"
              >
                <img
                  v-if="systemIcon"
                  :src="systemIcon"
                  class="h-7 w-7 object-contain"
                  alt=""
                />
                <span
                  v-else
                  class="text-xs font-semibold text-darkblue"
                >
                  {{ systemNameInitial }}
                </span>
              </div>

              <p class="text-sm text-darkblue font-medium flex-1 truncate">
                {{ systemName || 'System' }}
              </p>

              <span
                class="success-active-badge"
              >
                <span class="success-active-dot"></span>
                Active
              </span>
            </div>
          </div>

          <div class="flex items-center justify-end pt-4 mt-2 border-t border-lightgrey">
            <button type="button" class="screenshot-btn-manage" @click="onOk">
              OK
            </button>
          </div>
        </div>

        <!-- ===================== TASKS IN PROGRESS ===================== -->
        <div v-else-if="mode === 'tasks'">
          <div class="flex items-center gap-3">
            <div class="h-10 w-10 rounded-full bg-[#FFE8D6] flex items-center justify-center shrink-0">
              <img src="@/assets/warning.svg" class="h-6 w-6" alt="" />
            </div>
            <div class="flex-1">
              <h3 class="text-xl font-semibold text-darkblue">
                Tasks in progress
              </h3>
            </div>
          </div>

          <div>
            <p class="text-sm text-gray-600 mt-2">
              By clicking on <b>OK </b>button the following mentioned tasks in planner will be Terminated and the control will be deactivated.
              <br></br> To stop click on <b>CANCEL</b> button..

            </p>
          </div>

          <div class="pt-2 pb-2">
            <div class="controls-scroll border border-lightgrey rounded-md overflow-y-auto max-h-40">
              <div
                class="flex items-center gap-4 px-4 py-2 bg-[#f9f9fb] border-b border-lightgrey sticky top-0"
              >
                <p class="task-id-cell text-xs font-semibold tracking-wide text-placeholder uppercase">
                  Task Id
                </p>
                <p class="task-title-cell text-xs font-semibold tracking-wide text-placeholder uppercase">
                  Title
                </p>
                <p class="task-due-date-cell text-xs font-semibold tracking-wide text-placeholder uppercase">
                  Due Date
                </p>
              </div>
              <div
                v-for="(task, index) in normalizedTasks"
                :key="task.taskId || index"
                class="flex items-start gap-4 px-4 py-2.5 bg-white"
                :class="index !== normalizedTasks.length - 1 ? 'border-b border-lightgrey' : ''"
              >
                <p class="task-id-cell text-sm text-primary font-semibold">
                  <a
                    v-if="task.href"
                    :href="task.href"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="task-id-link"
                    @click.stop
                  >
                    {{ task.taskId }}
                  </a>
                  <span v-else>{{ task.taskId }}</span>
                </p>
                <p class="task-title-cell text-sm text-darkblue">
                  {{ task.taskTitle }}
                </p>
                <p class="task-due-date-cell text-sm text-darkblue shrink-0">
                  {{ task.dueDate }}
                </p>
              </div>
            </div>
          </div>

          <div class="flex items-center justify-end pt-3 border-t border-lightgrey gap-2">
            <button type="button" class="screenshot-btn-cancel" @click="onCancel">
              Cancel
            </button>
            <button type="button" class="screenshot-btn-manage" @click="onOk">
              OK
            </button>
          </div>
        </div>

        <!-- ===================== CONFIRM (EXISTING — UNCHANGED) ===================== -->
        <div v-else>
          <div class="flex items-center gap-3">
            <div class="h-10 w-10 rounded-full bg-[#FFE8D6] flex items-center justify-center shrink-0">
              <img src="@/assets/warning.svg" class="h-6 w-6" alt="" />
            </div>

            <div class="flex-1">
              <h3 class="text-xl font-semibold text-darkblue">
                Activate system?
              </h3>
            </div>
          </div>

          <div>
            <p class="text-sm text-gray-600 mt-2">
              You cannot activate while System is Inactive. Resolve the System listed below, then continue.
            </p>
          </div>

          <div class="pt-2 pb-2">
            <p class="text-xs font-semibold tracking-wide text-placeholder uppercase mb-2">
              System
            </p>

            <div class="controls-scroll border border-lightgrey rounded-md overflow-y-auto max-h-40">
              <div
                v-for="(control, index) in activeControls"
                :key="control._id || control.id || index"
                class="flex items-center gap-3 px-4 py-2.5 bg-white"
                :class="index !== activeControls.length - 1 ? 'border-b border-lightgrey' : ''"
              >
                <div
                  class="h-8 w-8 rounded border border-lightgrey flex items-center justify-center shrink-0 bg-white overflow-hidden"
                >
                  <img
                    v-if="control.imageUrl"
                    :src="control.imageUrl"
                    class="h-7 w-7 object-contain"
                    alt=""
                  />
                  <span v-else class="text-xs font-semibold text-darkblue">
                    {{ (control.systemName || 'S').charAt(0).toUpperCase() }}
                  </span>
                </div>

                <p class="text-sm text-darkblue">
                  {{ control.systemName }}
                </p>
              </div>
            </div>
          </div>

          <div class="flex items-center justify-end pt-3 border-t border-lightgrey gap-2">
            <button type="button" class="screenshot-btn-cancel" @click="onCancel">
              Cancel
            </button>

            <button type="button" class="screenshot-btn-manage" @click="onManageControls">
              Activate System
            </button>
          </div>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<style scoped>
.screenshot-btn-cancel {
  background-color: #ffffff;
  border: 1px solid #d1d5db;
  color: #374151;
  font-size: 14px;
  font-weight: 600;
  height: 32px;
  padding: 0 20px;
  border-radius: 4px;
}

/* same blue family as screenshot Manage controls */
.screenshot-btn-manage {
  background-color: #1976d2;
  color: #ffffff;
  font-size: 14px;
  font-weight: 600;
  height: 32px;
  padding: 0 20px;
  border-radius: 4px;
}
.success-active-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  font-weight: 600;
  padding: 4px 10px;
  border-radius: 9999px;
  color: #1976d2;
  background-color: #E3F2FD; /* light tint of OK blue */
  border: 1px solid #1976d2;
}

.success-active-dot {
  height: 6px;
  width: 6px;
  border-radius: 9999px;
  background-color: #1976d2;
}

/* thin scrollbar for controls list */
.controls-scroll {
  scrollbar-width: thin;              /* Firefox */
  scrollbar-color: #c4c4c4 transparent;
}

.controls-scroll::-webkit-scrollbar {
  width: 4px;                         /* Chrome/Edge/Safari */
}

.controls-scroll::-webkit-scrollbar-track {
  background: transparent;
}

.controls-scroll::-webkit-scrollbar-thumb {
  background-color: #c4c4c4;
  border-radius: 9999px;
}

.task-id-cell {
  width: 6.5rem;
  min-width: 6.5rem;
  flex-shrink: 0;
}

.task-title-cell {
  flex: 1;
  min-width: 0;
  white-space: normal;
  word-break: break-word;
  line-height: 1.4;
}

.task-due-date-cell {
  width: 6.5rem;
  min-width: 6.5rem;
  flex-shrink: 0;
}

.task-id-link {
  color: inherit;
  text-decoration: underline;
  cursor: pointer;
}

.task-id-link:hover {
  opacity: 0.85;
}
</style>

<script lang="ts">
import { defineComponent, PropType } from 'vue';
import dotsLoader from '@/components/dotsLoader.vue';
import moment from 'moment';

type ActiveControlItem = {
  _id?: string;
  id?: string;
  controlTitle?: string;
  title?: string;
  systemName?: string;
  imageUrl?: string;
};

type TaskItem = {
  taskId?: string;
  id?: string;
  _id?: string;
  plannerTaskId?: string;
  taskTitle?: string;
  title?: string;
  dueDate?: string | Date;
  status?: number | string;
  statusCode?: number | string;
};

type DialogMode = 'confirm' | 'loading' | 'success' | 'tasks';

export default defineComponent({
  name: 'MessageDialog',
  components: {
    dotsLoader,
  },
  props: {
    visible: { type: Boolean, default: false },
    activeControls: {
      type: Array as PropType<ActiveControlItem[]>,
      default: () => [],
    },
    /** confirm = existing UI; loading / success / tasks = additional states */
    mode: {
      type: String as PropType<DialogMode>,
      default: 'confirm',
    },
    systemName: { type: String, default: '' },
    systemIcon: { type: String, default: '' },
    loadingMessage: {
      type: String,
      default: 'Activating System please wait a moment',
    },
    /** In-progress planner tasks from /control/inactive 400 response */
    tasks: {
      type: Array as PropType<TaskItem[]>,
      default: () => [],
    },
  },
  emits: ['update:visible', 'close', 'cancel', 'continue', 'manage-controls', 'ok', 'ok-tasks'],
  computed: {
    systemNameInitial(): string {
      const name = (this.systemName || '').trim();
      return name ? name.charAt(0).toUpperCase() : 'S';
    },
    normalizedTasks(): { taskId: string; taskTitle: string; dueDate: string; href: string | null }[] {
      return (this.tasks || []).map((task: TaskItem) => ({
        taskId: String(task.taskId ?? task.id ?? 'N/A'),
        taskTitle: String(task.taskTitle ?? task.title ?? 'N/A'),
        dueDate: this.formatDueDate(task.dueDate),
        href: this.getPlannerTaskHref(task),
      }));
    },
  },
  watch: {
    visible(val: boolean) {
      document.body.style.overflow = val ? 'hidden' : '';
    },
  },
  methods: {
    getPlannerTaskObjectId(task: TaskItem): string | null {
      const raw = task.plannerTaskId ?? task._id ?? task.id;
      if (raw === undefined || raw === null || raw === '') {
        return null;
      }
      if (typeof raw === 'object' && raw !== null && '$oid' in (raw as object)) {
        return String((raw as { $oid: string }).$oid);
      }
      const id = String(raw);
      return id && id !== 'N/A' ? id : null;
    },
    getPlannerTaskHref(task: TaskItem): string | null {
      const plannerTaskId = this.getPlannerTaskObjectId(task);
      const plannerUi = (process.env.VUE_APP_PLANNER_UI || '').replace(/\/$/, '');
      if (!plannerTaskId || !plannerUi) {
        return null;
      }
      // Draft (10411) opens planner task overview (/task); otherwise subTask page
      const status = Number(task.statusCode ?? task.status);
      const pathSuffix = status === 10411 ? 'task' : 'subTask';
      return `${plannerUi}/org/process/${plannerTaskId}/${pathSuffix}`;
    },
    formatDueDate(time: string | Date | undefined): string {
      if (time === undefined || time === null || time === '') {
        return 'N/A';
      }
      const formatted = moment(time);
      return formatted.isValid() ? formatted.format('DD-MM-YYYY') : String(time);
    },
    closeDialog() {
      this.$emit('update:visible', false);
      this.$emit('close');
    },
    onCancel() {
      this.$emit('cancel');
      this.closeDialog();
    },
    onContinue() {
      if (this.activeControls.length > 0) return;
      this.$emit('continue');
      this.closeDialog();
    },
    onManageControls() {
      // Do NOT close here — parent switches to loading and calls API
      this.$emit('manage-controls');
    },
    onOk() {
      if (this.mode === 'tasks') {
        this.$emit('ok-tasks');
        return;
      }
      this.$emit('ok');
      this.closeDialog();
    },
  },
  beforeUnmount() {
    document.body.style.overflow = '';
  },
});
</script>